/**
 * React Query hooks for Workflow Builder
 *
 * AI-native workflow builder hooks with:
 * - Optimistic updates for instant UI feedback
 * - Proper cache invalidation
 * - AI suggestion handling
 * - Real-time validation
 */

import { useMemo, useCallback, useRef } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '../auth';
import { WorkflowBuilderService } from '../services/workflowBuilder';

// Query key factory for consistent key management
export const executionKeys = {
  all: ['executions'],
  lists: () => [...executionKeys.all, 'list'],
  list: (filters) => [...executionKeys.lists(), filters],
  details: () => [...executionKeys.all, 'detail'],
  detail: (id) => [...executionKeys.details(), id],
  events: (id) => [...executionKeys.detail(id), 'events'],
};

export const workflowKeys = {
  all: ['workflows'],
  lists: () => [...workflowKeys.all, 'list'],
  list: (filters) => [...workflowKeys.lists(), filters],
  details: () => [...workflowKeys.all, 'detail'],
  detail: (id) => [...workflowKeys.details(), id],
  versions: (id) => [...workflowKeys.detail(id), 'versions'],
  components: () => [...workflowKeys.all, 'components'],
  templates: () => [...workflowKeys.all, 'templates'],
  template: (id) => [...workflowKeys.templates(), id],
};

/**
 * Hook to get a memoized WorkflowBuilderService instance
 */
function useWorkflowBuilderService() {
  const { getToken } = useAuth();
  return useMemo(() => new WorkflowBuilderService(getToken), [getToken]);
}

// ============================================
// WORKFLOW HOOKS
// ============================================

/**
 * Hook to fetch list of workflows with filters
 * @param {Object} filters - Filter parameters
 * @param {Object} options - React Query options
 */
export function useWorkflows(filters = {}, options = {}) {
  const service = useWorkflowBuilderService();

  return useQuery({
    queryKey: workflowKeys.list(filters),
    queryFn: ({ signal }) => service.list(filters, { signal }),
    staleTime: 30000,
    ...options,
  });
}

/**
 * Hook to fetch a single workflow with all versions
 * @param {string} id - Workflow ID
 * @param {Object} options - React Query options
 */
export function useWorkflow(id, options = {}) {
  const service = useWorkflowBuilderService();

  return useQuery({
    queryKey: workflowKeys.detail(id),
    queryFn: ({ signal }) => service.get(id, { signal }),
    enabled: !!id,
    staleTime: 30000,
    ...options,
  });
}

/**
 * Hook to create a new workflow
 */
export function useCreateWorkflow() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => service.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

/**
 * Hook to update a workflow
 */
export function useUpdateWorkflow() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => service.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
    onError: (error) => {
      // Silently handle auto-save errors to prevent console noise
      console.warn('Workflow update failed:', error.message);
    },
  });
}

/**
 * Hook to delete a workflow
 */
export function useDeleteWorkflow() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => service.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

/**
 * Hook to unpublish a workflow (revert to draft)
 */
export function useUnpublishWorkflow() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => service.unpublish(id),
    onSuccess: (_, workflowId) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(workflowId) });
    },
  });
}

// ============================================
// VERSION HOOKS
// ============================================

/**
 * Hook to fetch workflow versions
 * @param {string} workflowId - Workflow ID
 * @param {Object} options - React Query options
 */
export function useWorkflowVersions(workflowId, options = {}) {
  const service = useWorkflowBuilderService();

  return useQuery({
    queryKey: workflowKeys.versions(workflowId),
    queryFn: ({ signal }) => service.listVersions(workflowId, { signal }),
    enabled: !!workflowId,
    staleTime: 30000,
    ...options,
  });
}

/**
 * Hook to create a new version
 */
export function useCreateVersion() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, data }) => service.createVersion(workflowId, data),
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(workflowId) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.versions(workflowId) });
    },
  });
}

/**
 * Hook to publish a workflow
 */
export function usePublishWorkflow() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, data }) => service.publish(workflowId, data),
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(workflowId) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

/**
 * Hook to preview a workflow (test without publishing)
 * Returns a URL that opens the SDK with the current workflow config
 */
export function usePreviewWorkflow() {
  const service = useWorkflowBuilderService();

  return useMutation({
    mutationFn: ({ workflowId, useCurrentConfig = true }) =>
      service.preview(workflowId, { use_current_config: useCurrentConfig }),
  });
}

/**
 * Hook to generate a shareable hosted verification link (/v/{token}) for a
 * published workflow. Returns { url, token, expires_at, workflow_name }.
 */
export function useCreateLiveLink() {
  const service = useWorkflowBuilderService();

  return useMutation({
    mutationFn: ({ workflowName, label, expiresHours }) =>
      service.createLiveLink(workflowName, { label, expiresHours }),
  });
}

/**
 * Hook to rollback to a previous version
 */
export function useRollbackWorkflow() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, versionId }) => service.rollback(workflowId, versionId),
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(workflowId) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.versions(workflowId) });
    },
  });
}

/**
 * Hook to get version diff
 * @param {string} workflowId - Workflow ID
 * @param {string} versionId - Version ID
 * @param {string} compareToId - Version to compare against
 * @param {Object} options - React Query options
 */
export function useVersionDiff(workflowId, versionId, compareToId, options = {}) {
  const service = useWorkflowBuilderService();

  return useQuery({
    queryKey: [...workflowKeys.versions(workflowId), 'diff', versionId, compareToId],
    queryFn: ({ signal }) =>
      service.getVersionDiff(workflowId, versionId, compareToId, { signal }),
    enabled: !!workflowId && !!versionId && !!compareToId,
    staleTime: 60000, // Diffs don't change
    ...options,
  });
}

// ============================================
// AI HOOKS
// ============================================

/**
 * Hook to get AI suggestion
 */
export function useAISuggestion() {
  const service = useWorkflowBuilderService();

  return useMutation({
    mutationFn: ({ workflowId, message, currentConfig, context }) =>
      service.getAISuggestion(workflowId, {
        message,
        current_config: currentConfig,
        context,
      }),
  });
}

/**
 * Hook to apply AI suggestion
 */
export function useApplyAISuggestion() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, suggestion, updatedConfig }) =>
      service.applyAISuggestion(workflowId, {
        suggestion,
        updated_config: updatedConfig,
      }),
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(workflowId) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.versions(workflowId) });
    },
  });
}

/**
 * Hook to validate workflow
 */
export function useValidateWorkflow() {
  const service = useWorkflowBuilderService();

  return useMutation({
    mutationFn: ({ workflowId, config }) =>
      service.validate(workflowId, { config }),
    onError: (error) => {
      console.warn('Validation error:', error.message);
    },
  });
}

// ============================================
// COMPONENT LIBRARY HOOKS
// ============================================

/**
 * Hook to fetch available components
 * @param {Object} options - React Query options
 */
export function useComponents(options = {}) {
  const service = useWorkflowBuilderService();

  return useQuery({
    queryKey: workflowKeys.components(),
    queryFn: ({ signal }) => service.listComponents({ signal }),
    staleTime: 300000, // Components rarely change, cache for 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch a single component
 * @param {string} componentId - Component ID
 * @param {Object} options - React Query options
 */
export function useComponent(componentId, options = {}) {
  const service = useWorkflowBuilderService();

  return useQuery({
    queryKey: [...workflowKeys.components(), componentId],
    queryFn: ({ signal }) => service.getComponent(componentId, { signal }),
    enabled: !!componentId,
    staleTime: 300000,
    ...options,
  });
}

// ============================================
// NATURAL LANGUAGE CHAT HOOKS
// ============================================

/**
 * Hook for conversational workflow building
 * Chat with AI to create/modify workflows using natural language
 */
export function useChatWorkflow() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ message, conversationId, workflowId, context }) =>
      service.chat({
        message,
        conversation_id: conversationId,
        workflow_id: workflowId,
        context,
      }),
    onSuccess: (data) => {
      // Invalidate workflow queries if a workflow was modified
      if (data.workflow_id) {
        queryClient.invalidateQueries({ queryKey: workflowKeys.detail(data.workflow_id) });
      }
    },
    onError: (error) => {
      // Error is handled by the component's try-catch
      console.warn('Chat API error:', error.message);
    },
  });
}

/**
 * Hook for parsing policy documents into workflows
 */
export function useParsePolicy() {
  const service = useWorkflowBuilderService();

  return useMutation({
    mutationFn: ({ policyText, industry, region }) =>
      service.parsePolicy({
        policy_text: policyText,
        industry: industry || 'general',
        region: region || 'global',
      }),
  });
}

/**
 * Hook for extracting requirements from policy files (Step 1 of 2-step flow)
 */
export function useExtractRequirements() {
  const service = useWorkflowBuilderService();

  return useMutation({
    mutationFn: ({ file, industry, region }) =>
      service.extractRequirements(file, {
        industry: industry || 'general',
        region: region || 'global',
      }),
    onError: (error) => {
      console.warn('Requirements extraction error:', error?.message || error);
    },
  });
}

/**
 * Hook for generating workflow from confirmed requirements (Step 2 of 2-step flow)
 */
export function useGenerateFromRequirements() {
  const service = useWorkflowBuilderService();

  return useMutation({
    mutationFn: ({ requirements, industry, region }) =>
      service.generateFromRequirements({
        requirements,
        industry: industry || 'general',
        region: region || 'global',
      }),
    onError: (error) => {
      console.warn('Workflow generation error:', error?.message || error);
    },
  });
}

/**
 * Hook for migrating a competitor workflow from a screenshot using AI vision
 */
export function useMigrateFromScreenshot() {
  const service = useWorkflowBuilderService();

  return useMutation({
    mutationFn: ({ imageFile, explanation, sourcePlatform }) =>
      service.migrateFromScreenshot(imageFile, explanation, sourcePlatform),
    onError: (error) => {
      console.warn('Workflow migration error:', error?.message || error);
    },
  });
}

/**
 * Hook for uploading policy files (PDF/DOCX/TXT/MD) and parsing into workflows
 */
export function useUploadPolicyFile() {
  const service = useWorkflowBuilderService();

  return useMutation({
    mutationFn: ({ file, industry, region, savePolicy }) =>
      service.uploadPolicyFile(file, {
        industry: industry || 'general',
        region: region || 'global',
        save_policy: savePolicy !== false,
      }),
    onError: (error) => {
      console.warn('Policy upload API error:', error?.message || error);
    },
  });
}

// ============================================
// EXECUTION HOOKS
// ============================================

/**
 * Hook to list workflow executions with filters
 */
export function useExecutions(filters = {}) {
  const service = useWorkflowBuilderService();

  return useQuery({
    queryKey: executionKeys.list(filters),
    queryFn: () => service.listExecutions(filters),
    refetchInterval: filters.status === 'in_progress' ? 5000 : 30000, // Poll faster for active
  });
}

/**
 * Hook to get a single execution with details
 */
export function useExecution(executionId) {
  const service = useWorkflowBuilderService();

  return useQuery({
    queryKey: executionKeys.detail(executionId),
    queryFn: () => service.getExecution(executionId),
    enabled: !!executionId,
    refetchInterval: 5000, // Auto-refresh for live monitoring
  });
}

/**
 * Hook to get execution events (audit trail)
 */
export function useExecutionEvents(executionId) {
  const service = useWorkflowBuilderService();

  return useQuery({
    queryKey: executionKeys.events(executionId),
    queryFn: () => service.getExecutionEvents(executionId),
    enabled: !!executionId,
    refetchInterval: 5000,
  });
}

// ============================================
// CUSTOM COMPONENT HOOKS
// ============================================

/**
 * Hook to create a custom component
 */
export function useCreateComponent() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => service.createComponent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.components() });
    },
  });
}

/**
 * Hook to update a custom component
 */
export function useUpdateComponent() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ componentId, data }) => service.updateComponent(componentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.components() });
    },
  });
}

/**
 * Hook to delete a custom component
 */
export function useDeleteComponent() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (componentId) => service.deleteComponent(componentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.components() });
    },
  });
}

// ============================================
// TEMPLATE HOOKS
// ============================================

/**
 * Hook to fetch workflow templates
 * @param {Object} filters - { category, industry, workflow_type }
 * @param {Object} options - React Query options
 */
export function useTemplates(filters = {}, options = {}) {
  const service = useWorkflowBuilderService();

  return useQuery({
    queryKey: workflowKeys.templates(),
    queryFn: ({ signal }) => service.listTemplates(filters, { signal }),
    staleTime: 300000, // Templates rarely change, cache for 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch a single template
 * @param {string} templateId - Template ID
 * @param {Object} options - React Query options
 */
export function useTemplate(templateId, options = {}) {
  const service = useWorkflowBuilderService();

  return useQuery({
    queryKey: workflowKeys.template(templateId),
    queryFn: ({ signal }) => service.getTemplate(templateId, { signal }),
    enabled: !!templateId,
    staleTime: 300000,
    ...options,
  });
}

/**
 * Hook to create a workflow from a template
 */
export function useCreateFromTemplate() {
  const service = useWorkflowBuilderService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, data }) => service.createFromTemplate(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

// ============================================
// AUTO-SAVE HOOK
// ============================================

/**
 * Hook for debounced auto-save of workflow changes
 * @param {string} workflowId - Workflow ID
 * @param {number} delay - Debounce delay in ms (default: 2000)
 */
export function useAutoSaveWorkflow(workflowId, delay = 2000) {
  const updateMutation = useUpdateWorkflow();
  const timeoutRef = useRef(null);

  const save = useCallback(
    (config) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        updateMutation.mutate({
          id: workflowId,
          data: { config },
        });
      }, delay);
    },
    [workflowId, delay, updateMutation]
  );

  const saveImmediately = useCallback(
    (config) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      return updateMutation.mutateAsync({
        id: workflowId,
        data: { config },
      });
    },
    [workflowId, updateMutation]
  );

  return {
    save,
    saveImmediately,
    isSaving: updateMutation.isPending,
    error: updateMutation.error,
  };
}
